import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Icon } from '@iconify/react';
import { useSelector } from 'react-redux';
import moment from "moment";
import { checkPermission } from "@comps/commonFunctions";
import { EmptyComponent, StatusTag, CountryFlag } from "@comps/components";
import { statusClass } from "@comps/data/StatusTag";
import { getDashboardDataReq } from "./requests";
import { getUserCasesReq } from "@mods/userCase/requests";
import { makeRequest } from '@utils/helpers';
import "./dashboard.css";

/**
 * One entry per counter returned by admin-management/get-admin-dashboard.
 * `tone` names a .status-* class in tokens.css, so a tile and the rows of
 * the list it links to wear exactly the same colour.
 */
const STATS = [
  {
    key: 'total',
    label: 'Total cases',
    hint: 'Everything you can see',
    icon: 'tabler:folders',
    to: '/cases/total',
    tone: 'neutral',
  },
  {
    key: 'new',
    label: 'New cases',
    hint: 'Submitted today, not yet taken',
    icon: 'tabler:inbox',
    to: '/cases/new',
    tone: 'new',
  },
  {
    key: 'modifications',
    label: 'Modifications',
    hint: 'Sent back for changes',
    icon: 'tabler:edit-circle',
    to: '/cases/modifications',
    tone: 'modification',
  },
  {
    key: 'approved',
    label: 'Approved',
    hint: 'Signed off by the doctor',
    icon: 'tabler:circle-check',
    to: '/cases/approved',
    tone: 'approved',
  },
  {
    key: 'overdue',
    label: 'Overdue',
    hint: 'Waiting more than 24 hours',
    icon: 'tabler:clock-exclamation',
    to: '/cases/total?overdue=1',
    tone: 'overdue',
    // Admin-only: the endpoint sends no count at all to a doctor or a
    // technician, and the tile drops out rather than reading zero.
    adminOnly: true,
  },
];

/**
 * "Dr. Sana Iqbal" should greet as "Sana", not "Dr." — drop a leading
 * honorific before taking the first name.
 */
const TITLES = ['dr', 'dr.', 'mr', 'mr.', 'mrs', 'mrs.', 'ms', 'ms.', 'prof', 'prof.'];

const firstNameOf = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length > 1 && TITLES.includes(parts[0].toLowerCase())) return parts[1];
  return parts[0];
};

/** What share of every case this counter accounts for, 0-100. */
const shareOf = (data, key) => {
  if (!data || !data.total) return 0;
  return Math.round(((data[key] || 0) / data.total) * 100);
};

/**
 * The three tracked states as a proportion of the whole, plus whatever is
 * left over (taken, rejected) so the bar always adds up to the total
 * rather than quietly under-reporting it.
 */
const splitOf = (data) => {
  if (!data || !data.total) return [];
  const tracked = ['new', 'modifications', 'approved'];
  const parts = tracked.map((key, i) => ({
    tone: ['new', 'modification', 'approved'][i],
    percent: ((data[key] || 0) / data.total) * 100,
  }));
  const rest = 100 - parts.reduce((sum, p) => sum + p.percent, 0);
  if (rest > 0.5) parts.push({ tone: 'neutral', percent: rest });
  return parts.filter((p) => p.percent > 0);
};

/**
 * The per-country counts the API sends back, each with its share of the
 * biggest country so the bars are comparable against each other rather
 * than against the total (one dominant country would otherwise flatten
 * every other bar to a sliver).
 */
const countryRows = (data) => {
  const rows = (data && data.by_country) || [];
  if (!rows.length) return [];
  const top = Math.max(...rows.map((r) => r.total)) || 1;
  return rows.map((row) => ({
    ...row,
    // The label is CountryFlag's to draw now — it pairs the name with
    // the flag so the two can never say different things.
    percent: Math.round((row.total / top) * 100),
    share: data.total ? Math.round((row.total / data.total) * 100) : 0,
  }));
};

const greeting = () => {
  const hour = moment().hour();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const Dashboard = () => {
  const [show, setShow] = useState(false);
  // Both start true: their requests fire from the effect below, i.e.
  // after the first paint. Starting false meant the very first frame
  // rendered the settled UI — tiles reading 0 and "Every new case has
  // been picked up" — which was then replaced by the skeletons a beat
  // later, so the dashboard appeared to load backwards.
  const [loader, setLoader] = useState(true);
  const [attentionLoader, setAttentionLoader] = useState(true);
  const [data, setData] = useState(null);
  const [attention, setAttention] = useState([]);
  const authUser = useSelector((state) => state.auth.authUser);

  useEffect(() => {
    if (!checkPermission("dashboard_index")) {
      window.location.href = "/cases/total";
      return;
    }
    setShow(true);
    makeRequest(setLoader, getDashboardDataReq, null, setData, Function);
    // the cases that most need a person: newest unclaimed ones
    makeRequest(
      setAttentionLoader,
      getUserCasesReq,
      { type: 'new', page: 1, limit: 5 },
      (res) => setAttention(res.data || []),
      Function
    );
  }, []);

  if (!show) return null;

  const firstName = firstNameOf(authUser && authUser.name);
  const countries = countryRows(data);

  return (
    <div className="dashboard">
      <header className="dash-head">
        <p className="dash-eyebrow">{ moment().format('dddd, D MMMM') }</p>
        <h1 className="dash-title">
          { greeting() }{ firstName ? `, ${firstName}` : '' }
        </h1>
        <p className="dash-sub">Here is where your cases stand right now.</p>
      </header>

      <div className="dash-tiles">
        { STATS.filter((stat) => !stat.adminOnly || (data && data[stat.key] !== null && data[stat.key] !== undefined)).map((stat) => {
          const value = (data && data[stat.key]) ?? 0;
          return (
            <Link key={ stat.key } className={ `dash-tile status-${stat.tone}` } to={ stat.to }>
              <span className="dash-tile-top">
                <span className="dash-tile-mark">
                  <Icon icon={ stat.icon } width={ 20 } />
                </span>
                <span className="dash-tile-label">{ stat.label }</span>
                <Icon className="dash-tile-arrow" icon="tabler:arrow-up-right" width={ 18 } />
              </span>

              <span className="dash-tile-value">
                { loader ? <span className="dash-tile-skeleton" /> : value }
              </span>

              {/* A count means little on its own: 14 new cases reads very
                  differently against 40 total than against 400. The bar
                  gives each number its denominator. */ }
              { stat.key === 'total'
                ? <span className="dash-tile-foot">
                  <span className="dash-split" aria-hidden="true">
                    { splitOf(data).map((part) => (
                      <span
                        key={ part.tone }
                        className={ `dash-split-part status-${part.tone}` }
                        style={ { width: `${part.percent}%` } }
                      />
                    )) }
                  </span>
                  <span className="dash-tile-hint">{ stat.hint }</span>
                </span>
                : <span className="dash-tile-foot">
                  <span className="dash-bar">
                    <span className="dash-bar-fill" style={ { width: `${shareOf(data, stat.key)}%` } } />
                  </span>
                  <span className="dash-tile-hint">
                    { loader || !data
                      ? stat.hint
                      : `${shareOf(data, stat.key)}% of all cases` }
                  </span>
                </span> }
            </Link>
          );
        }) }
      </div>

      {/* Admin-only: the API returns an empty breakdown for anyone whose
          view is already limited to one country's cases. */ }
      { countries.length > 0 &&
        <section className="dash-panel dash-panel-stacked">
          <div className="dash-panel-head">
            <div>
              <h2 className="dash-panel-title">Cases by country</h2>
              <p className="dash-panel-sub">Where the cases you can see came from.</p>
            </div>
          </div>

          <ul className="dash-countries">
            { countries.map((row) => (
              <li className="dash-country" key={ row.country || 'unknown' }>
                <span className="dash-country-name">
                  <CountryFlag code={ row.country } fallback="No country on file" />
                </span>
                <span className="dash-country-bar">
                  <span className="dash-country-fill" style={ { width: `${row.percent}%` } } />
                </span>
                <span className="dash-country-count">{ row.total }</span>
                <span className="dash-country-share">{ row.share }%</span>
              </li>
            )) }
          </ul>
        </section>
      }

      <section className="dash-panel">
        <div className="dash-panel-head">
          <div>
            <h2 className="dash-panel-title">Needs your attention</h2>
            <p className="dash-panel-sub">The newest cases nobody has picked up yet.</p>
          </div>
          <Link className="dash-panel-link" to="/cases/new">
            View all new cases <Icon icon="tabler:arrow-right" width={ 16 } />
          </Link>
        </div>

        { attentionLoader
          ? <div className="dash-rows">
            { [0, 1, 2, 3, 4].map((i) => (
              <div className="dash-row is-loading" key={ i }>
                <span className="dash-row-skeleton" style={ { width: '32%' } } />
                <span className="dash-row-skeleton" style={ { width: '18%' } } />
                <span className="dash-row-skeleton" style={ { width: '14%' } } />
              </div>
            )) }
          </div>
          : attention.length === 0
            ? <EmptyComponent
              icon="tabler:checks"
              title="You're all caught up"
              description="Every new case has been picked up. New submissions will show here."
            />
            : <div className="dash-rows">
              { attention.map((each) => (
                <Link
                  key={ each.id }
                  to={ `/cases/details/${each.id}` }
                  className={ `dash-row ${statusClass(each.status)}` }
                >
                  <span className="dash-row-name">{ each.name || "Unknown patient" }</span>
                  <span className="dash-row-type">{ each.type?.name || "No case type" }</span>
                  <span className="dash-row-id mono">{ each.case_number || "—" }</span>
                  <span className="dash-row-when">{ moment(each.created_at).fromNow() }</span>
                  <StatusTag status={ each.status } />
                </Link>
              )) }
            </div> }
      </section>
    </div>
  );
};

export default Dashboard;
